import { Button } from '@base-ui/react/button';
import {
  faFileLines,
  faGraduationCap,
} from '@fortawesome/free-solid-svg-icons';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Frame, MediaPicker } from '@/admin/content/MediaPicker';
import { apiErrorDetail } from '@/admin/errorDetail';
import buttonStyles from '@/admin/styles/button.module.css';
import { ConfirmDialog } from '@/admin/ui/ConfirmDialog';
import {
  CourseOutlineLayout,
  courseOf,
  LessonHeader,
} from '@/admin/ui/ContentNav';
import { DraftAncestorNotice } from '@/admin/ui/DraftAncestorNotice';
import {
  DangerGroup,
  DetailPage,
  EditorLayout,
  EditorMain,
  EditorPanel,
  FormSection,
  PanelGroup,
  PanelLabel,
  SaveGroup,
} from '@/admin/ui/Editor';
import { useEditMode, validateEditSearch } from '@/admin/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import {
  CheckboxLabel,
  ErrorText,
  FieldHint,
  FormLabel,
  FormRow,
  Select,
  TextInput,
} from '@/admin/ui/Form';
import { QuizPublishWarning, quizPublishMessage } from '@/admin/ui/QuizLock';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import { useUnsavedChanges } from '@/admin/ui/useUnsavedChanges';
import {
  clampPosition,
  deletePrompt,
  EditConflictNotice,
  editConflict,
  LiveEditNotice,
  NeedsNewVersion,
  useCourseVersions,
} from '@/admin/ui/Versions';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';

type LessonAdmin = components['schemas']['LessonAdmin'];

export const Route = createFileRoute('/admin/lessons/$lessonId')({
  component: LessonDetail,
  validateSearch: validateEditSearch,
});

function LessonDetail() {
  const { t } = useTranslation();
  const { lessonId } = Route.useParams();
  const lessonIdNum = Number(lessonId);
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);

  // Admin-only read: the student lesson_detail is gated to a fully published
  // path, so a draft lesson would 404 there.
  const admin = api.adminGetLesson.useQuery({
    path: { lesson_id: lessonIdNum },
  });

  if (admin.isLoading)
    return <Message>{t('admin.lessons.loadingOne')}</Message>;
  if (admin.error || !admin.data)
    return <Message $error>{t('admin.lessons.errorLoadingOne')}</Message>;

  const lesson = admin.data;

  return (
    <DetailPage>
      <LessonHeader
        lesson={lesson}
        nav={!editing}
        action={
          !editing && (
            <Button className={buttonStyles.Button} onClick={startEditing}>
              {t('admin.common.edit')}
            </Button>
          )
        }
      />

      {editing ? (
        <LessonEditor key={lesson.id} lesson={lesson} onDone={stopEditing} />
      ) : (
        <CourseOutlineLayout course={courseOf(lesson.path)} lesson={lesson.id}>
          <Section>
            <SectionTitle>{t('admin.common.overview')}</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>{t('admin.common.id')}</FieldLabel>
                <FieldValue>{lesson.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.slug')}</FieldLabel>
                <FieldValue>{lesson.slug || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.order')}</FieldLabel>
                <FieldValue>{lesson.order}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.status')}</FieldLabel>
                <FieldValue>{lesson.status}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.lessons.passingScore')}</FieldLabel>
                <FieldValue>{lesson.passing_score}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.lessons.autoGrade')}</FieldLabel>
                <FieldValue>
                  {lesson.auto_grade
                    ? t('admin.common.yes')
                    : t('admin.common.no')}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.lessons.course')}</FieldLabel>
                <FieldValue>
                  <Link
                    to="/admin/courses/$courseId/$version/"
                    params={{
                      courseId: String(lesson.course_id),
                      version: String(lesson.course_version),
                    }}
                  >
                    #{lesson.course_id} v{lesson.course_version}
                  </Link>
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.nav.topics')}</FieldLabel>
                <FieldValue>{lesson.topics_count}</FieldValue>
              </Field>
            </FieldGrid>
            {lesson.thumbnail && (
              <ThumbField>
                <FieldLabel as="div">{t('admin.courses.thumbnail')}</FieldLabel>
                <Frame>
                  <img
                    src={lesson.thumbnail}
                    alt={t('admin.courses.thumbnailAlt', {
                      title: lesson.title,
                      interpolation: { escapeValue: false },
                    })}
                  />
                </Frame>
              </ThumbField>
            )}
          </Section>
        </CourseOutlineLayout>
      )}
    </DetailPage>
  );
}

function LessonEditor({
  lesson,
  onDone,
}: {
  lesson: LessonAdmin;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState(lesson.title);
  const [slug, setSlug] = useState(lesson.slug);
  const [order, setOrder] = useState(String(lesson.order));
  const [status, setStatus] = useState(lesson.status);
  const [passingScore, setPassingScore] = useState(
    String(lesson.passing_score),
  );
  const [autoGrade, setAutoGrade] = useState(lesson.auto_grade);
  const [thumbnail, setThumbnail] = useState({
    key: lesson.thumbnail_key,
    url: lesson.thumbnail,
  });
  const [uploading, setUploading] = useState(false);
  // Null until changed: the field shows where the lesson already is.
  const [position, setPosition] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  // A published version's lessons keep their order and status; those change in a
  // new version. Wording still edits in place.
  const frozen = lesson.version_state !== 'draft';
  const { data: versions } = useCourseVersions(lesson.course_id);
  const inDraftVersion =
    lesson.version_state === 'draft' &&
    Boolean(versions?.some((v) => v.state === 'live'));

  const course = courseOf(lesson.path);
  const outline = api.adminGetCourseOutline.useQuery(
    {
      path: { course_id: course?.id ?? 0 },
      query: { version: course?.version ?? 1 },
    },
    { enabled: inDraftVersion && course !== null },
  );
  const currentIndex =
    outline.data?.lessons.findIndex((l) => l.id === lesson.id) ?? -1;
  const lastPosition = outline.data?.lessons.length ?? 1;
  const shownPosition =
    position ?? (currentIndex >= 0 ? String(currentIndex + 1) : '');

  const formValues = JSON.stringify([
    title,
    slug,
    order,
    status,
    passingScore,
    autoGrade,
    thumbnail.key,
    position,
  ]);
  const [savedValues] = useState(formValues);
  const dirty = formValues !== savedValues;
  const {
    allowLeave,
    confirmDiscard,
    dialog: unsavedDialog,
  } = useUnsavedChanges(dirty);

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: api.adminGetLesson.getQueryKey({
        path: { lesson_id: lesson.id },
      }),
    });
    queryClient.invalidateQueries({
      queryKey: api.adminListLessons.getQueryKey({
        path: { course_id: lesson.course_id },
      }),
    });
  };

  const updateMutation = api.adminUpdateLesson.useMutation(undefined, {
    onSuccess: () => {
      allowLeave();
      invalidate();
      onDone();
    },
  });

  const deleteMutation = api.adminDeleteLesson.useMutation(undefined, {
    onSuccess: () => {
      allowLeave();
      queryClient.invalidateQueries({
        queryKey: api.adminListLessons.getQueryKey({
          path: { course_id: lesson.course_id },
        }),
      });
      navigate({
        to: '/admin/lessons/',
        search: { course: lesson.course_id, version: lesson.course_version },
      });
    },
  });

  const trimmedTitle = title.trim();

  // Publishing stamps the lesson for good, so its quiz lock is confirmed first. In
  // a draft of a live course nothing locks until the version itself is published.
  const locksQuiz =
    !inDraftVersion &&
    status === 'published' &&
    lesson.status !== 'published' &&
    !lesson.quiz_locked;

  // Pinned when editing starts: a refetch (e.g. on tab focus) must not advance it,
  // or a save over someone else's change would slip past the conflict check.
  const [baseRevision] = useState(lesson.revision);
  const handleSave = () => {
    // Block save while a thumbnail is still uploading, or the old key persists
    // and the just-picked image is silently dropped.
    if (!trimmedTitle || updateMutation.isPending || uploading) return;
    if (locksQuiz && !confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    updateMutation.mutate({
      path: { lesson_id: lesson.id },
      body: {
        title: trimmedTitle,
        slug,
        // In a draft, Position places it; a stale order would move it back.
        ...(frozen
          ? {}
          : inDraftVersion
            ? { status }
            : { order: Number(order), status }),
        ...(inDraftVersion && position ? { position: Number(position) } : {}),
        passing_score: Number(passingScore),
        auto_grade: autoGrade,
        thumbnail_key: thumbnail.key,
        expected_revision: baseRevision,
      },
    });
  };

  const handleDelete = () => {
    if (!window.confirm(deletePrompt('lesson', lesson))) return;
    deleteMutation.mutate({ path: { lesson_id: lesson.id } });
  };

  const reload = () => {
    allowLeave();
    invalidate();
    onDone();
  };

  return (
    <EditorLayout>
      <EditorMain>
        <LiveEditNotice state={lesson.version_state} />
        <FormSection
          tone="content"
          icon={faFileLines}
          title={t('admin.common.details')}
          description={t('admin.lessons.detailsDescription')}
        >
          <FormRow>
            <FormLabel htmlFor="lesson-title">
              {t('admin.common.title')}
            </FormLabel>
            <TextInput
              id="lesson-title"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
            />
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="lesson-slug">
              {t('admin.common.slug')}
            </FormLabel>
            <TextInput
              id="lesson-slug"
              value={slug}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSlug(e.target.value)
              }
            />
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="lesson-thumbnail">
              {t('admin.courses.thumbnail')}
            </FormLabel>
            <MediaPicker
              id="lesson-thumbnail"
              value={thumbnail}
              onChange={setThumbnail}
              onUploadingChange={setUploading}
              alt={t('admin.lessons.thumbnailInputAlt')}
            />
          </FormRow>
        </FormSection>

        <FormSection
          tone="answers"
          icon={faGraduationCap}
          title={t('admin.lessons.grading')}
          description={t('admin.lessons.gradingDescription')}
        >
          <FormRow>
            <FormLabel htmlFor="lesson-passing">
              {t('admin.lessons.passingScore')}
            </FormLabel>
            <TextInput
              id="lesson-passing"
              type="number"
              value={passingScore}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPassingScore(e.target.value)
              }
            />
          </FormRow>
          <CheckboxLabel htmlFor="lesson-autograde">
            <input
              id="lesson-autograde"
              type="checkbox"
              checked={autoGrade}
              onChange={(e) => setAutoGrade(e.target.checked)}
            />
            {t('admin.lessons.autoGrade')}
          </CheckboxLabel>
        </FormSection>
      </EditorMain>

      <EditorPanel aria-label={t('admin.series.saveAndSettings')}>
        <SaveGroup
          heading={<PanelLabel>{t('admin.lessons.panelLabel')}</PanelLabel>}
          dirty={dirty}
          saving={updateMutation.isPending}
          saveDisabled={uploading || !trimmedTitle}
          onSave={handleSave}
          onCancel={() => confirmDiscard(onDone)}
        >
          {!trimmedTitle && (
            <FieldHint>{t('admin.lessons.giveTitle')}</FieldHint>
          )}
          {updateMutation.isError &&
            (editConflict(updateMutation.error) ? (
              <EditConflictNotice
                error={updateMutation.error}
                onReload={reload}
              />
            ) : (
              <ErrorText>
                {apiErrorDetail(updateMutation.error) ??
                  t('admin.common.couldNotSave')}
              </ErrorText>
            ))}
        </SaveGroup>

        {frozen ? (
          <PanelGroup>
            <NeedsNewVersion
              target={{
                courseId: lesson.course_id,
                version: lesson.course_version,
                item: { entity: 'lesson', lineageId: lesson.lineage_id },
              }}
            >
              {t('admin.lessons.frozenNotice')}
            </NeedsNewVersion>
            <FieldHint>
              {t('admin.lessons.statusOrder', {
                status: lesson.status,
                order: lesson.order,
                interpolation: { escapeValue: false },
              })}
            </FieldHint>
          </PanelGroup>
        ) : (
          <PanelGroup>
            <FormRow>
              <FormLabel htmlFor="lesson-status">
                {t('admin.common.status')}
              </FormLabel>
              <Select
                id="lesson-status"
                value={status}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setStatus(e.target.value as LessonAdmin['status'])
                }
              >
                <option value="draft">draft</option>
                <option value="published">published</option>
              </Select>
              {inDraftVersion && status === 'draft' && (
                <FieldHint>{t('admin.lessons.draftHidden')}</FieldHint>
              )}
              {locksQuiz && (
                <QuizPublishWarning
                  parent="lesson"
                  publishedCount={lesson.quiz_published_count}
                  draftCount={lesson.quiz_draft_count}
                />
              )}
              <DraftAncestorNotice
                entityLabel="lesson"
                ancestors={[
                  // In a draft version the course is a draft by definition.
                  ...(inDraftVersion
                    ? []
                    : [
                        {
                          label: t('admin.entity.course'),
                          status: lesson.course_status,
                        },
                      ]),
                  {
                    label: t('admin.entity.series'),
                    status: lesson.series_status,
                  },
                ]}
              />
            </FormRow>
            {inDraftVersion ? (
              <FormRow>
                <FormLabel htmlFor="lesson-position">
                  {t('admin.lessons.positionInCourse')}
                </FormLabel>
                <TextInput
                  id="lesson-position"
                  type="number"
                  min={1}
                  max={lastPosition}
                  value={shownPosition}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPosition(e.target.value)
                  }
                  onBlur={() =>
                    position !== null &&
                    setPosition(clampPosition(position, lastPosition))
                  }
                />
              </FormRow>
            ) : (
              <FormRow>
                <FormLabel htmlFor="lesson-order">
                  {t('admin.common.order')}
                </FormLabel>
                <TextInput
                  id="lesson-order"
                  type="number"
                  value={order}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOrder(e.target.value)
                  }
                />
              </FormRow>
            )}
          </PanelGroup>
        )}

        {!frozen && (
          <DangerGroup>
            <Button
              className={`${buttonStyles.Button} ${buttonStyles.Danger}`}
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending
                ? t('admin.common.deleting')
                : t('admin.lessons.delete')}
            </Button>
            {deleteMutation.isError && (
              <ErrorText>
                {apiErrorDetail(deleteMutation.error) ??
                  t('admin.lessons.deleteError')}
              </ErrorText>
            )}
          </DangerGroup>
        )}
      </EditorPanel>

      <ConfirmDialog
        open={confirming}
        title={t('admin.series.confirmTitle')}
        confirmLabel={t('admin.common.publish')}
        onConfirm={handleSave}
        onCancel={() => setConfirming(false)}
      >
        <p>
          {quizPublishMessage({
            parent: 'lesson',
            publishedCount: lesson.quiz_published_count,
            draftCount: lesson.quiz_draft_count,
          })}
        </p>
        <p>{t('admin.lessons.returningNote')}</p>
      </ConfirmDialog>
      {unsavedDialog}
    </EditorLayout>
  );
}

const ThumbField = styled.div`
  padding: 0 var(--inner-padding) var(--inner-padding);
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
